import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { usePatchReferenceRateValue } from 'src/features/referenceRateValues/api/usePatchReferenceRateValue';
import { LIST_REFERENCE_RATE_VALUES_RESOURCE } from 'src/features/referenceRateValues/api/useListReferenceRateValues';
import { REFERENCE_RATE_VALUE_RESOURCE } from 'src/features/referenceRateValues/api/useGetReferenceRateValue';
import { REFERENCE_RATE_RESOURCE } from 'src/features/referenceRates/api/useGetReferenceRate';
import { LIST_REFERENCE_RATES_RESOURCE } from 'src/features/referenceRates/api/useListReferenceRates';
import { referenceRateValuesMessages } from 'src/features/referenceRateValues/utils/messages';
import { ReferenceRateValue } from 'src/features/referenceRateValues/utils/types';
import ReferenceRateValueFormModal from 'src/features/referenceRateValues/components/forms/ReferenceRateValueFormModal';

interface UpdateReferenceRateValueFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedReferenceRateValue: ReferenceRateValue;
    children?: React.ReactNode;
}

function UpdateReferenceRateValueFormModal<TData>({
    className,
    children,
    selectedReferenceRateValue,
}: UpdateReferenceRateValueFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { referenceRateId = '' } = useParams();

    const { mutate, isPending } = usePatchReferenceRateValue({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATE_VALUES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [REFERENCE_RATE_VALUE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [REFERENCE_RATE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATES_RESOURCE] });
            toast({
                title: formatMessage(referenceRateValuesMessages.updateReferenceRateValueSuccess),
                description: formatMessage(referenceRateValuesMessages.updateReferenceRateValueSuccessDescription),
            });
        },
    });

    return (
        <ReferenceRateValueFormModal
            selectedReferenceRateValue={selectedReferenceRateValue}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, referenceRateId, valueId: selectedReferenceRateValue.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(referenceRateValuesMessages.value).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(referenceRateValuesMessages.editReferenceRateValueSubtitle)}
        >
            {children}
        </ReferenceRateValueFormModal>
    );
}

export default UpdateReferenceRateValueFormModal;
